<template>
  <div class="download-temp-file-container">
    <el-card class="box-card">
      <div slot="header" class="flex justify-between">
        <span>{{ $t('downloadTempFile.title') }}</span>
        <el-button type="primary" size="mini" style="float: right;" @click="_queryDownloadTempFileMethod">
          {{ $t('downloadTempFile.refresh') }}
        </el-button>
      </div>

      <el-table v-loading="loading" :data="downloadTempFileInfo.files" border style="width: 100%">
        <el-table-column :label="$t('downloadTempFile.name')" align="center">
          <template slot-scope="scope">
            {{ _displayName(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column prop="fileTypeName" :label="$t('downloadTempFile.fileType')" align="center" />
        <el-table-column prop="downloadUserName" :label="$t('downloadTempFile.downloadUser')" align="center" />
        <el-table-column prop="createTime" :label="$t('downloadTempFile.downloadTime')" align="center" />
        <el-table-column prop="stateName" :label="$t('downloadTempFile.status')" align="center" />
        <el-table-column prop="remark" :label="$t('downloadTempFile.remark')" align="center" />
        <el-table-column :label="$t('downloadTempFile.operation')" align="center" width="200">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === '3003'" size="mini" type="primary" @click="_downLoadFile(scope.row)">
              {{ $t('downloadTempFile.download') }}
            </el-button>
            <el-button v-if="scope.row.state === '3003' || scope.row.state === '4004'" size="mini" type="danger"
              @click="_openDeleteFileModel(scope.row)">
              {{ $t('downloadTempFile.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination :current-page.sync="page.current" :page-sizes="[10, 20, 30, 50]" :page-size="page.size"
        :total="page.total" layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
        @current-change="handleCurrentChange" />
    </el-card>

    <delete-download-temp-file ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { listUserDownloadFile } from '@/api/system/downloadTempFileApi'
import DeleteDownloadTempFile from '@/components/system/deleteDownloadTempFile'
import { getCommunityId } from '@/api/community/communityApi'
import {
  buildDownloadFileName,
  downloadViaBackend,
  fetchAndDownload,
  getBusinessName
} from '@/utils/downloadNameUtil'

export default {
  name: 'DownloadTempFileList',
  components: {
    DeleteDownloadTempFile
  },
  data() {
    return {
      loading: false,
      downloadTempFileInfo: {
        files: [],
        conditions: {
          communityId: '',
          page: 1,
          row: 10
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.downloadTempFileInfo.conditions.communityId = getCommunityId()
    this._listFiles(this.page.current, this.page.size)
  },
  methods: {
    async _listFiles(page, size) {
      try {
        this.loading = true
        this.downloadTempFileInfo.conditions.page = page
        this.downloadTempFileInfo.conditions.row = size

        const { data, total } = await listUserDownloadFile(this.downloadTempFileInfo.conditions)
        this.downloadTempFileInfo.files = data
        this.page.total = total
      } catch (error) {
        this.$message.error(this.$t('downloadTempFile.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _downLoadFile(file) {
      if (!file) {
        return
      }
      const downloadName = buildDownloadFileName(file)
      // 优先走下载中心自己的下载接口（同源），fetch + Blob + <a download>
      // 由前端决定落盘文件名，100% 命中中文业务名。
      // 失败时再 fallback 到原有 tempUrl 流程，不影响旧数据和其它调用方。
      if (file.downloadId) {
        downloadViaBackend(file, downloadName)
          .catch(() => {
            // 后端下载接口失败（token 过期、接口 404 等），降级走原始 tempUrl 流程。
            // 不阻断用户操作，仅给一个轻提示。
            this.$message.warning(this.$t('downloadTempFile.backendDownloadFailed'))
            this._fallbackDownload(file, downloadName)
          })
        return
      }
      this._fallbackDownload(file, downloadName)
    },
    _fallbackDownload(file, downloadName) {
      if (!file.tempUrl && !file.downloadUrl) {
        this.$message.error(this.$t('downloadTempFile.downloadFailed'))
        return
      }
      const url = file.downloadUrl || file.tempUrl
      const sameOrigin = (() => {
        try {
          return new URL(url, window.location.href).origin === window.location.origin
        } catch (e) {
          return false
        }
      })()
      if (sameOrigin) {
        const a = document.createElement('a')
        a.href = url
        a.download = downloadName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        return
      }
      // 跨源：先用 fetch + Blob 强制按中文名下载，兜底覆盖后端未读取 downloadName 的情况。
      // 如果 fetch 失败（鉴权/CORS 等），回退到 window.open 让浏览器按响应头/URL 决定文件名。
      fetchAndDownload(url, downloadName).catch(() => {
        const separator = url.indexOf('?') >= 0 ? '&' : '?'
        window.open(url + separator + 'downloadName=' + encodeURIComponent(downloadName), '_blank')
      })
    },
    _displayName(file) {
      const businessName = getBusinessName(file)
      if (file && file.createTime) {
        return businessName + '（' + file.createTime + '）'
      }
      return businessName
    },
    _openDeleteFileModel(file) {
      this.$refs.deleteDialog.open(file)
    },
    _queryDownloadTempFileMethod() {
      this.page.current = 1
      this._listFiles(this.page.current, this.page.size)
    },
    handleSuccess() {
      this._listFiles(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listFiles(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listFiles(val, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.download-temp-file-container {
	padding: var(--ds-spacing-lg);
	background-color: var(--ds-gray-100);
	min-height: calc(100vh - 60px);

	::v-deep .el-card {
		border-radius: var(--ds-radius-md);
		border: 1px solid var(--ds-gray-300);
		box-shadow: var(--ds-shadow-sm);
		margin-bottom: var(--ds-spacing-lg);
	}

	::v-deep .el-card__header {
		padding: var(--ds-spacing-md) var(--ds-spacing-lg);
		font-weight: 600;
		color: var(--ds-primary);
		background-color: var(--ds-gray-100);
		border-bottom: 1px solid var(--ds-gray-300);
	}

	.box-card {
		margin-bottom: var(--ds-spacing-lg);
	}

	::v-deep .el-pagination {
		margin-top: var(--ds-spacing-md);
		text-align: right;
	}
}
</style>